import { useRef } from "react";
import { signIn } from "aws-amplify/auth";
import type { PortalAuthClientWithSelfUI } from "@/lib/portalAuthClient";
import {
  clearRememberMeFromStorage,
  setRememberMeToStorage,
  setLastLoginId,
  getLastLoginId,
  clearPasskeyOnThisBrowser,
  hasPasskeyOnThisBrowser,
  markPasskeyOnThisBrowser,
} from "@/utils/storage";
import {
  isPasskeyCancel,
  needsServerPasskeyCheck,
  passkeyCheckOutcome,
  passkeyLoginMode,
  shouldPromptPasskeySetup,
} from "../passkeyLoginMode";
import { AUTH_LOGIN_OPTIONS } from "@/utils/const";
import type {
  LoginOptionsRequest,
  LoginOptionsResponse,
} from "@shared-domain/auth-service/contract";
import { validatePasswordRequirements } from "@/utils/passwordValidation";
import { translateCognitoMessage } from "@/utils/cognitoErrorMessages";
import {
  ADMIN_REISSUE_MESSAGE,
  canRequestReissue,
  isNoResetDestinationError,
  pickResetUsername,
} from "../resetPasswordTarget";
import { requestPasswordReissue } from "@/lib/passwordReissueApi";
import { isPlatformAuthenticatorAvailable } from "@/utils/webauthnSupport";
import { pickTenantWithoutAsking, returnToForProduct } from "../resumeTarget";
import type { useLoginState } from "./useLoginState";

/**
 * PRODUCT_DEV-396 ログイン画面で login-options API (認証不要) を呼び出してパスキー利用可否を取得
 * 失敗時は false 扱い（フォールバックでパスワード入力）
 */
async function fetchPasskeyAvailable(email: string): Promise<boolean> {
  try {
    const body: LoginOptionsRequest = { email };
    const res = await fetch(AUTH_LOGIN_OPTIONS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as LoginOptionsResponse;
    return Boolean(data?.passkeyEnabled);
  } catch (err) {
    console.warn("login-options fetch failed:", err);
    return false;
  }
}

/**
 * 認証エラーのハンドリング共通ユーティリティ
 * Cognito/Amplify のエラーオブジェクトから message と recoverySuggestion を抽出して state にセットする
 */
export const handleAuthError = (
  err: unknown,
  state: { setError: (s: string) => void; setRecoverySuggestion: (s: string | null) => void },
  fallbackMessage: string,
) => {
  console.error(err);
  const error = err as { message?: string; recoverySuggestion?: string };
  const translated = translateCognitoMessage(error.message);
  if (translated) {
    state.setError(translated);
    state.setRecoverySuggestion(null);
    return;
  }
  state.setError(error.message ?? fallbackMessage);
  state.setRecoverySuggestion(error.recoverySuggestion ?? null);
};

/**
 * PRODUCT_DEV-396 パスキー登録促進モーダルを表示すべきか判定
 * テスト用: forcePasskeyPrompt="1"=常に表示 / "0"=常に非表示
 *
 * 判定の中身は `shouldPromptPasskeySetup`（passkeyLoginMode.ts）。PRODUCT_DEV-1121 で「このブラウザで登録済み」を
 * サーバーの `currentDeviceHasPasskey`（ブラウザと OS の種類だけで判定）からこのブラウザの印に替えた。
 * サーバーにパスキーがあるかは、30 日の決まりだけで答えが出ないときだけ Cognito に聞く。
 */
export async function shouldShowPasskeyPrompt(args: {
  client: PortalAuthClientWithSelfUI;
  forcePrompt: string | null;
  lastPasskeyPromptedAt: string | undefined;
  loginId: string | null;
}): Promise<boolean> {
  const { client, forcePrompt, lastPasskeyPromptedAt, loginId } = args;

  // テスト用クエリオーバーライド
  if (forcePrompt === "1") return true;
  if (forcePrompt === "0") return false;

  if (!client.me) return false;
  const base = {
    tenantAllowsPasskey: client.me.memberships.some((m) => m.tenantPasskeyEnabled),
    markedOnThisBrowser: hasPasskeyOnThisBrowser(loginId),
    lastPasskeyPromptedAt,
  };
  let hasPasskeyOnServer: boolean | undefined;
  if (needsServerPasskeyCheck(base)) {
    try {
      hasPasskeyOnServer = (await client.listPasskeys()).length > 0;
    } catch (err) {
      console.warn("listPasskeys for prompt failed:", err);
    }
  }
  return shouldPromptPasskeySetup({ ...base, hasPasskeyOnServer });
}

/**
 * ログインページの全てのハンドラー関数を集約したカスタムフック
 */
export const useLoginHandlers = (
  client: PortalAuthClientWithSelfUI,
  state: ReturnType<typeof useLoginState>,
  tenantIdFromQuery: string | null,
  productIdFromQuery: string | null,
  _mfaFromQuery: string | null,
  forcePasskeyPromptFromQuery: string | null = null,
  // PRODUCT_DEV-421 alias ログインモード時の tenantId (URL `/:tenantId` or `?tenant=` 由来)
  aliasTenantId: string | null = null,
  /**
   * PRODUCT_DEV-641: 配信ホストだけでテナントが決まる環境の tenantId（ビルド時に確定）。
   * 無印のように複数テナントが同居する環境では**常に null**。
   */
  fixedTenantId: string | null = null,
  /**
   * PRODUCT_DEV-1129: セッション切れの再ログインでプロダクトが付けてくる
   * `?lastTenantId=` と `?returnTo=`（扱いは `resumeTarget.ts`）。
   */
  resume: { lastTenantId: string | null; returnTo: string | null } = {
    lastTenantId: null,
    returnTo: null,
  },
  /** PRODUCT_DEV-1238: 会社の `passwordReissue`（ログイン画面のテナントブランドと一緒に取る）。 */
  reissue: { enabled: boolean } = { enabled: false },
) => {
  /**
   * PRODUCT_DEV-641: **このログインで入るテナントが既に決まっているか。**
   *
   * `?tenantId=` はプロダクト側から明示されたテナント（従来からの絞り込み）。
   * `fixedTenantId` は配信ホストで決まるテナント。どちらも「選ばせる余地が無い」
   * ので同じ扱いにする。**両方 null のときは従来どおり**（絞り込まない）。
   *
   * ⚠ `aliasTenantId` はここに入れない。あれは「どのテナントの独自 ID として
   * 名前解決するか」であって、所属テナントの絞り込みとは別の判断
   * （変えると無印のエイリアスログインの挙動が変わる）。
   *
   * **配信ホスト由来を先に見る。** 固定は環境の不変条件で、クエリより強い。
   * `shared-ui/loaders/auth-loader.ts` も `fixedTenantId || exchangeTenantId || …`
   * の順にしてあり、ここを逆にすると**同じ画面で Portal と product の判断が食い違う**
   * （専用ドメイン上で `?tenantId=` を付けると、別テナントで選択画面まで進める）。
   */
  const forcedTenantId = fixedTenantId ?? tenantIdFromQuery;

  /**
   * PRODUCT_DEV-641: **サインインが走り出しているか**を live に持つ。
   *
   * `canAutoTrigger()` が見ている `state.loading` 等は素の useState で、
   * `handleEmailBlur` は**レンダー時のスナップショットを閉じ込める**。
   * `await client.resolveAlias(...)`（サーバ側で最低 200ms 待つ）の後に評価しても
   * 値は古いままなので、「ID 欄にフォーカスしたままログインボタンを押す」
   * （mousedown → blur → click → submit）で、パスワードのサインインと
   * パスキーの自動起動が**同時に走りうる**。ref なら await をまたいでも今の値が読める。
   */
  const signInInFlight = useRef(false);
  /**
   * PRODUCT_DEV-1121: サインインを試した回数。パスキーの有無の問い合わせを待つ間に
   * サインインが1回でも走ったかを、問い合わせの前後で比べて知る（終わった後でも分かる）。
   */
  const signInAttempts = useRef(0);
  const beginSignIn = () => {
    signInInFlight.current = true;
    signInAttempts.current += 1;
  };

  /** PRODUCT_DEV-641: 成否に関わらず必ず呼ぶ（下ろし忘れるとパスキー自動起動が二度と動かない）。 */
  const endSignIn = () => {
    signInInFlight.current = false;
  };

  /**
   * ユーザ情報の取得～テナント選択画面またはリダイレクト
   */
  const whoamiAndCheckProduct = async (options?: { skipPasskeySetup?: boolean }) => {
    state.setLoadingMessage("ユーザ情報取得中...");

    // /me 呼び出し
    await client.whoami();

    if (!client.me) {
      throw new Error("ユーザ情報の取得に失敗しました。");
    }

    // PRODUCT_DEV-396 パスキー登録促進モーダル
    if (
      !state.showPasskeyPromptModal &&
      !options?.skipPasskeySetup &&
      !state.passkeyLoginCancelled
    ) {
      const shouldPrompt = await shouldShowPasskeyPrompt({
        client,
        forcePrompt: forcePasskeyPromptFromQuery,
        lastPasskeyPromptedAt: client.me.lastPasskeyPromptedAt,
        loginId: getLastLoginId(),
      });
      if (shouldPrompt) {
        // Platform Authenticator (Windows Hello / Touch ID 等) が無いと
        // associateWebAuthnCredential() が QR コードでクロスデバイス認証を出すため、
        // 事前判定で利用不可ならモーダル自体を表示しない。
        // lastPasskeyPromptedAt は更新せず、端末追加で利用可能になったら次回表示する。
        const platformAvailable = await isPlatformAuthenticatorAvailable();
        if (platformAvailable) {
          state.setShowPasskeyPromptModal(true);
          state.setLoading(false);
          state.setLoadingMessage("");
          return;
        }
      }
    }

    state.setLoadingMessage("サービス情報取得中...");

    // テナント一覧を構築
    const memberships = client.me.memberships;

    // テナントが既に決まっている場合はフィルタ（クエリ指定 or 配信ホストによる固定）
    const filteredMemberships = forcedTenantId
      ? memberships.filter((m) => m.tenantId === forcedTenantId)
      : memberships;

    if (filteredMemberships.length === 0) {
      // PRODUCT_DEV-641: 固定環境では「テナントが1つも無い」ではなく
      // 「このURLのテナントに所属していない」が実態。原因が読めない文言だと
      // 利用者も問い合わせ対応も詰まる。
      throw new Error(
        fixedTenantId && !tenantIdFromQuery
          ? "このURLではご利用いただけないアカウントです。"
          : "利用可能なテナントがありません。",
      );
    }

    const tenantList = filteredMemberships
      .map((m) => ({
        tenantId: m.tenantId,
        tenantName: m.tenantName,
        role: m.role,
        mfaRequired: m.mfaRequired,
        products: m.products,
      }))
      .sort((a, b) => a.tenantName.localeCompare(b.tenantName, "ja"));

    // テナント1件 → 自動選択。
    // PRODUCT_DEV-1129: 直前に使っていたテナントに所属していて、そこで頼まれたプロダクトを
    // 使えるときも選び直させない。それ以外（共用 PC で別の人・テナントから外された・
    // 契約が外れた）は普通の選択画面へ。
    const autoTenant = pickTenantWithoutAsking(tenantList, {
      forcedTenantId,
      lastTenantId: resume.lastTenantId,
      productId: productIdFromQuery,
    });

    if (autoTenant) {
      await handleTenantChoice(autoTenant);
    } else {
      // 複数テナント → 選択画面表示
      state.setTenants(tenantList);
      state.setShowTenantSelect(true);
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * テナント選択後の処理: MFAチェック → プロダクト選択
   */
  const handleTenantChoice = async (
    tenant: import("../types").LoginPageTenant,
  ) => {
    state.setSelectedTenant(tenant);
    state.setShowTenantSelect(false);
    state.setLoading(true);
    state.setLoadingMessage("テナント情報を確認中...");

    // MFA チェック: テナントの mfaRequired が true の場合、TOTP 未設定なら Setup へ
    if (tenant.mfaRequired) {
      const { isTOTPEnabled } = await client.checkMFAPreference();
      if (!isTOTPEnabled) {
        // TOTP 未設定 → セットアップ画面へ
        const { totpUri, secretKey } = await client.setupTOTP();
        state.setTotpUri(totpUri);
        state.setTotpSecretKey(secretKey);
        state.setShowTOTPSetup(true);
        state.setLoading(false);
        state.setLoadingMessage("");
        return;
      }
    }

    // プロダクト一覧を構築（選択テナント内のみ）
    await showProductsForTenant(tenant);
  };

  /**
   * テナント内のプロダクト一覧表示
   */
  const showProductsForTenant = async (
    tenant: import("../types").LoginPageTenant,
  ) => {
    // クエリパラメータでプロダクトが指定されている場合はフィルタ
    let products = tenant.products;
    if (productIdFromQuery) {
      products = products.filter((p) => p.id === productIdFromQuery);
      if (products.length === 0) {
        throw new Error("許可されていないプロダクトです。");
      }
    }

    const availableProducts = products
      .map((p) => ({
        ...p,
        tenantId: tenant.tenantId,
        tenantName: tenant.tenantName,
        isLoading: false,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "ja"));

    if (availableProducts.length === 0) {
      throw new Error("利用中のプロダクトはありません。");
    } else if (availableProducts.length === 1) {
      // プロダクト1件 → 自動選択して即リダイレクト
      client.setAutoRedirect(true);
      const { id: productId, url, tenantId } = availableProducts[0];
      await handleProductChoice({ productId, url, tenantId });
    } else {
      // 複数プロダクト → 選択UI表示
      state.setProducts(availableProducts);
      state.setShowSelectUI(true);
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * プロダクトを1つ選んだあとの共通処理
   * - One-time code 発行
   * - リダイレクト
   */
  const handleProductChoice = async (props: {
    url: string;
    productId: string;
    tenantId: string;
  }) => {
    await client.issueOneTimeCode(props.productId, props.tenantId);
    await client.redirectProductUrl(props.url, {
      returnTo: returnToForProduct({
        returnTo: resume.returnTo,
        lastTenantId: resume.lastTenantId,
        chosenTenantId: props.tenantId,
      }),
    });
  };

  /**
   * ログインボタン押下時の処理
   * - ID/PWでのサインイン
   * - ログインユーザ情報の取得
   * - 遷移先プロダクトの特定
   * - 遷移先が1つの場合、プロダクトURLにリダイレクト
   */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);
    state.setLoading(true);
    beginSignIn();

    try {
      // サインイン
      state.setLoadingMessage("ログイン中...");
      // PRODUCT_DEV-421: alias モード時は resolve-alias 経由
      if (aliasTenantId) {
        await client.signInByAlias(aliasTenantId, state.email, state.password);
      } else {
        await client.signIn(state.email, state.password);
      }

      // チャレンジが必要な場合はチャレンジUIを表示
      if (client.challengeType) {
        state.setChallengeType(client.challengeType);

        // RESET_PASSWORDチャレンジの場合、リセットコード入力画面用にメールアドレスを設定
        if (client.challengeType === "RESET_PASSWORD") {
          state.setResetEmail(state.email);
          // PRODUCT_DEV-891: 独自ID で入った人は Cognito のユーザー名で確定させる
          // （独自ID のままコード確認に渡すと、正しいコードでも失敗する）。
          const resolved = aliasTenantId
            ? await client.resolveAlias(aliasTenantId, state.email).catch(() => null)
            : null;
          state.setResetUsername(pickResetUsername(state.email, resolved));
        }

        state.setLoading(false);
        state.setLoadingMessage("");
        return;
      }

      // 「次回から自動的にログイン」の場合はストレージに保存
      setRememberMeToStorage(state.rememberMe);
      // パスキーログイン用にログインIDを保存
      setLastLoginId(state.email);

      await whoamiAndCheckProduct();
    } catch (err) {
      handleAuthError(err, state, "ログインに失敗しました");
      // PRODUCT_DEV-549: ログイン失敗で signOut(revoke) すると、既存の有効な共有 refreshToken が
      // 失効し他 product が落ちるため呼ばない。
      clearRememberMeFromStorage();
    } finally {
      endSignIn();
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * 新しいパスワード設定の処理
   */
  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (state.newPassword !== state.confirmPassword) {
      state.setError("パスワードが一致しません。");
      return;
    }

    const newPasswordError = validatePasswordRequirements(state.newPassword);
    if (newPasswordError) {
      state.setError(newPasswordError);
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("パスワード設定中...");

    try {
      await client.confirmChallenge(state.newPassword);

      // チャレンジが完了した場合
      if (!client.challengeType) {
        // 🔥 重要: 状態をクリアして画面遷移を可能にする
        state.setChallengeType(null);

        // 「次回から自動的にログイン」の場合はストレージに保存
        setRememberMeToStorage(state.rememberMe);
        // パスキーログイン用にログインIDを保存
        setLastLoginId(state.email);

        await whoamiAndCheckProduct();
      } else {
        // さらにチャレンジが必要な場合
        state.setChallengeType(client.challengeType);
      }
    } catch (err) {
      handleAuthError(err, state, "パスワード設定に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * MFAコード確認の処理
   */
  const handleMfaCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (state.mfaCode.length !== 6) {
      state.setError("6桁のコードを入力してください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("確認中...");

    try {
      await client.confirmChallenge(state.mfaCode);

      // チャレンジが完了した場合
      if (!client.challengeType) {
        // 🔥 重要: 状態をクリアして画面遷移を可能にする
        state.setChallengeType(null);

        // 「次回から自動的にログイン」の場合はストレージに保存
        setRememberMeToStorage(state.rememberMe);
        // パスキーログイン用にログインIDを保存
        setLastLoginId(state.email);

        await whoamiAndCheckProduct();
      } else {
        // さらにチャレンジが必要な場合
        state.setChallengeType(client.challengeType);
      }
    } catch (err) {
      handleAuthError(err, state, "認証コードの確認に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * メールコード確認の処理
   */
  const handleEmailCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (state.emailCode.length !== 6) {
      state.setError("6桁のコードを入力してください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("確認中...");

    try {
      await client.confirmChallenge(state.emailCode);

      // チャレンジが完了した場合
      if (!client.challengeType) {
        state.setChallengeType(null);
        setRememberMeToStorage(state.rememberMe);
        // パスキーログイン用にログインIDを保存
        setLastLoginId(state.email);
        await whoamiAndCheckProduct();
      } else {
        // さらにチャレンジが必要な場合
        state.setChallengeType(client.challengeType);
      }
    } catch (err) {
      handleAuthError(err, state, "認証コードの確認に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * チャレンジキャンセルの処理
   */
  const handleChallengeCancel = async () => {
    state.setChallengeType(null);
    state.setNewPassword("");
    state.setConfirmPassword("");
    state.setMfaCode("");
    state.setError(null);
    state.setRecoverySuggestion(null);
    await client.signOut({ reason: "challenge-cancel" });
    clearRememberMeFromStorage();
  };

  /**
   * パスワードリセットの開始
   */
  const handleForgotPassword = () => {
    state.setResetFlowStep("email");
    state.setError(null);
    state.setRecoverySuggestion(null);
  };

  /**
   * リセットコード送信の処理
   */
  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    const typed = state.resetEmail.trim();
    if (!typed) {
      state.setError("メールアドレスまたは ID を入力してください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("リセットコードを送信中...");

    try {
      /**
       * PRODUCT_DEV-891: 独自ID でも再設定できるようにする。
       *
       * 独自ID の画面ではまず Cognito のユーザー名に引き当てる。見つからなければ
       * 入力値をメールアドレス（ログインID）として扱う（ログインの signInByAlias と同じ）。
       * 引き当ての失敗（通信エラー等）も「見つからない」に倒す —— 再設定は
       * 入力値のままでも試せるので、ここで止める理由が無い。
       */
      const resolved = aliasTenantId
        ? await client.resolveAlias(aliasTenantId, typed).catch(() => null)
        : null;
      const username = pickResetUsername(typed, resolved);
      state.setResetUsername(username);
      await client.resetPassword(username);
      state.setResetFlowStep("code");
      state.setError(null);
    } catch (err) {
      if (isNoResetDestinationError(err)) {
        // メールが無い人と、見つからない人に**同じ案内**を出す（在籍者を割り出させない）。
        if (aliasTenantId && canRequestReissue(aliasTenantId, reissue.enabled)) {
          // PRODUCT_DEV-1238 案C: そのまま受け付ける（間に確認の画面は挟まない）。サーバが、
          // メールの無い人 → 管理者への依頼 / 初期パスワードのままでメールのある人 → 招待メールの送り直し、
          // に振り分ける。見つからない ID も同じ応答・同じ画面（ID があるかを知られない）
          try {
            await requestPasswordReissue({ tenantId: aliasTenantId, loginId: typed });
            state.setResetFlowStep("reissueSent");
            state.setError(null);
          } catch (reqErr) {
            state.setError(reqErr instanceof Error ? reqErr.message : String(reqErr));
          }
        } else {
          state.setError(ADMIN_REISSUE_MESSAGE);
        }
        state.setRecoverySuggestion(null);
      } else {
        handleAuthError(err, state, "リセットコードの送信に失敗しました");
      }
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * パスワードリセット完了の処理
   */
  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (state.resetCode.length !== 6) {
      state.setError("6桁のコードを入力してください。");
      return;
    }

    if (state.resetPassword !== state.resetConfirmPassword) {
      state.setError("パスワードが一致しません。");
      return;
    }

    const resetPasswordError = validatePasswordRequirements(state.resetPassword);
    if (resetPasswordError) {
      state.setError(resetPasswordError);
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("パスワードをリセット中...");

    try {
      await client.confirmResetPassword(
        // PRODUCT_DEV-891: 送信時に引き当てた Cognito ユーザー名。無ければ入力値。
        state.resetUsername || state.resetEmail,
        state.resetCode,
        state.resetPassword
      );

      state.setEmail(state.resetEmail);
      state.setResetFlowStep(null);
      state.setResetEmail("");
      state.setResetUsername("");
      state.setResetCode("");
      state.setResetPassword("");
      state.setResetConfirmPassword("");
      state.setError(null);
      state.setRecoverySuggestion(
        "パスワードがリセットされました。新しいパスワードでログインしてください。",
      );
    } catch (err) {
      handleAuthError(err, state, "パスワードリセットに失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * TOTP セットアップの完了処理
   */
  const handleTOTPSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    if (state.totpVerifyCode.length !== 6) {
      state.setError("6桁のコードを入力してください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("設定中...");

    try {
      await client.verifyAndEnableTOTP(state.totpVerifyCode);

      // 設定完了 → サインアウトして再ログインを要求
      state.setShowTOTPSetup(false);
      state.setTotpUri(null);
      state.setTotpSecretKey(null);
      state.setTotpVerifyCode("");
      await client.signOut({ reason: "totp-complete" });
      clearRememberMeFromStorage();
      state.setRecoverySuggestion("二段階認証の設定が完了しました。再度ログインしてください。");
    } catch (err) {
      handleAuthError(err, state, "認証コードの検証に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * TOTP セットアップのキャンセル処理
   */
  const handleTOTPSetupCancel = async () => {
    state.setShowTOTPSetup(false);
    state.setTotpUri(null);
    state.setTotpSecretKey(null);
    state.setTotpVerifyCode("");
    state.setError(null);
    state.setRecoverySuggestion(null);
    await client.signOut({ reason: "totp-cancel" });
    clearRememberMeFromStorage();
  };

  /**
   * パスキーでログインする処理
   * @param emailOverride - state.emailの代わりに使用するログインID（自動試行用）
   * @param displayIdOverride - PRODUCT_DEV-641: **次回のために保存する ID**。
   *   alias モードでは `emailOverride` に `cognitoUsername`（`<local部>.<tenantId>`）が
   *   入るが、これを保存すると次回その値が入力欄に prefill され、
   *   (1) 専用ドメインで隠したはずの tenantId が画面に出る
   *   (2) その値では alias も email も一致せず**パスキー自動起動が発火しなくなる**
   *   （＝2 回目以降だけ体験が壊れる）。保存するのは利用者が実際に打った値。
   */
  const handlePasskeyLogin = async (
    emailOverride?: string,
    displayIdOverride?: string,
  ) => {
    state.setError(null);
    state.setRecoverySuggestion(null);

    const email = emailOverride || state.email;
    if (!email) {
      state.setError("ログインIDを入力してからパスキーでログインしてください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("パスキー認証中...");
    beginSignIn();

    try {
      const result = await signIn({
        username: email,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "WEB_AUTHN",
        },
      });

      if (result.isSignedIn) {
        // 成功: トークンを取得・保存してからプロダクト選択へ
        await client.handleSuccessfulSignIn(" (passkey)");
        setRememberMeToStorage(state.rememberMe);
        setLastLoginId(displayIdOverride ?? email);
        // PRODUCT_DEV-1121: このブラウザでパスキーが使えた印。次からは自動で起動する
        markPasskeyOnThisBrowser(displayIdOverride ?? email);
        await whoamiAndCheckProduct({ skipPasskeySetup: true });
      } else if (result.nextStep) {
        // チャレンジが必要な場合
        state.setChallengeType(result.nextStep.signInStep);
      }
    } catch (err) {
      console.log("Passkey login cancelled or failed:", err);
      // キャンセル → フラグを立てて静かにフォールバック（印は残す。次に開いたときはまた自動で起動する）
      // それ以外（パスキー未登録など）→ ID/PWログイン後にパスキー登録を促す
      if (isPasskeyCancel(err)) {
        state.setPasskeyLoginCancelled(true);
      } else {
        // PRODUCT_DEV-1121: 登録が無いと分かったので印を消す（次から自動で起動しない）
        clearPasskeyOnThisBrowser({ loginId: displayIdOverride ?? email });
        state.setRecoverySuggestion(
          "パスキーが登録されていません。ID/パスワードでログイン後、パスキーを登録できます。",
        );
      }
    } finally {
      endSignIn();
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * PRODUCT_DEV-396 ログインID入力欄からフォーカスが外れた時 / autofill 検知時にパスキー利用可否を判定
   * login-options API (認証不要) を呼び出して passkeyAvailable を更新し、利用可なら自動的にパスキー認証を開始する。
   * ユーザがWebAuthnダイアログをキャンセルしたら handlePasskeyLogin 側で passkeyLoginCancelled が立ち、
   * 以降は ID/PW フォーム経由のログインに自然にフォールバックする。
   */
  const handleEmailBlur = async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed || trimmed.length < 3) {
      state.setPasskeyAvailable(null);
      return;
    }
    // ログイン済み（製品へ移動中など）なら問い合わせない。ここでパスキーを出しても使い道が無い
    if (client.me) return;

    const attemptsAtStart = signInAttempts.current;

    // PRODUCT_DEV-1121: サーバーの「あり」だけでは自動で起動しない。サーバーはブラウザと OS の種類でしか
    // 端末を見分けられず、パスキーの無いブラウザで OS の「利用可能なパスキーがありません」や QR コードが出るため。
    // このブラウザに印があるときだけ自動。無ければ何も出さず、ログイン後の案内で聞く
    const applyPasskeyMode = async (
      serverAvailable: boolean,
      username: string,
      displayId: string,
    ) => {
      // 返事を待つ間にログインが動き出していたら、処理中の画面にパスキーの画面を割り込ませない
      const outcome = passkeyCheckOutcome({
        signedIn: !!client.me,
        signInInFlight: signInInFlight.current,
        signInTriedSinceCheck: signInAttempts.current !== attemptsAtStart,
      });
      if (outcome === "skip") return;

      state.setPasskeyAvailable(serverAvailable);
      const mode = passkeyLoginMode({
        serverAvailable,
        markedOnThisBrowser: hasPasskeyOnThisBrowser(displayId),
      });
      if (mode === "auto" && canAutoTrigger()) {
        await handlePasskeyLogin(username, displayId);
      }
    };

    // 自動トリガー可否の共通ガード
    // ローディング中・チャレンジ進行中・モーダル表示中・既ログイン中は起動しない
    // (既ログイン中に signIn を再実行すると テナント選択画面で無限ループに陥るため
    //  client.me ガード必須)
    const canAutoTrigger = () =>
      !signInInFlight.current &&
      !state.passkeyLoginCancelled &&
      !state.loading &&
      !state.challengeType &&
      !state.showPasskeyPromptModal &&
      !client.me;

    // PRODUCT_DEV-484 alias モード: まず alias 解決を試みる。
    // 解決できれば cognitoUsername でパスキー判定/自動起動。
    // 解決できなければ（= 通常ログインIDを入力）下の email ベース判定にフォールスルー。
    if (aliasTenantId) {
      try {
        const { resolved, cognitoUsername, passkeyEnabled } =
          await client.resolveAlias(aliasTenantId, trimmed);
        if (resolved) {
          // サインインは cognitoUsername、保存・表示は打たれた alias。
          await applyPasskeyMode(passkeyEnabled, cognitoUsername, trimmed);
          return;
        }
        // alias 未解決 → 通常ログインID(email) 扱いでフォールスルー
      } catch (err) {
        console.warn("resolveAlias failed:", err);
        // フォールスルー
      }
    }

    const available = await fetchPasskeyAvailable(trimmed);
    await applyPasskeyMode(available, trimmed, trimmed);
  };

  /**
   * PRODUCT_DEV-396 促進モーダルで「あとで」を選択した場合
   * lastPasskeyPromptedAt を現在時刻で更新し、モーダルを閉じてフローを継続
   */
  /**
   * 案内を出したことをサーバーに残す（lastPasskeyPromptedAt）。PRODUCT_DEV-1121: 登録できたとき・
   * 「すでにある」のときも残す。印はブラウザのキャッシュと一緒に消えることがあり、残さないと
   * 毎回のログインで案内が出てしまうため
   */
  const recordPasskeyPrompted = async () => {
    const tenantId = client.me?.currentTenantId ?? client.me?.memberships[0]?.tenantId ?? "";
    if (!tenantId) return;
    try {
      const { apiPatch } = await import("@shared-ui/utils/api");
      const { AUTH_ME } = await import("@/utils/const");
      await apiPatch(AUTH_ME, {
        tenantId,
        lastPasskeyPromptedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.warn("Failed to update lastPasskeyPromptedAt:", err);
    }
  };

  const handlePasskeyPromptLater = async () => {
    state.setShowPasskeyPromptModal(false);
    await recordPasskeyPrompted();
    // モーダルを閉じてフロー継続
    state.setLoading(true);
    try {
      await whoamiAndCheckProduct({ skipPasskeySetup: true });
    } catch (err) {
      handleAuthError(err, state, "処理に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * PRODUCT_DEV-396 促進モーダルで「設定する」を選択した場合
   * モーダル内で直接 WebAuthn 登録ダイアログを起動 (1クリック化)。
   * 成功 → モーダル閉じてプロダクト選択へ進む。
   * キャンセル/失敗 → 「あとで」と同等に lastPasskeyPromptedAt を更新してフロー継続。
   */
  const handlePasskeyPromptSetup = async () => {
    state.setError(null);
    state.setRecoverySuggestion(null);
    state.setLoading(true);
    state.setLoadingMessage("パスキーを登録中...");
    try {
      await client.registerPasskey();
      await recordPasskeyPrompted();
      state.setShowPasskeyPromptModal(false);
      state.setLoadingMessage("サービス情報取得中...");
      await whoamiAndCheckProduct({ skipPasskeySetup: true });
    } catch (err) {
      console.warn("Passkey registration cancelled or failed:", err);
      // キャンセル/失敗は「あとで」扱いで30日抑止 + フロー継続
      await handlePasskeyPromptLater();
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * パスワードリセット画面の戻る処理
   */
  const handleResetBack = () => {
    state.setResetFlowStep("email");
    state.setResetCode("");
    state.setResetPassword("");
    state.setResetConfirmPassword("");
    state.setError(null);
    state.setRecoverySuggestion(null);
  };

  /**
   * パスワードリセットのキャンセル処理
   */
  const handleResetCancel = () => {
    state.setResetFlowStep(null);
    state.setResetEmail("");
    state.setResetUsername("");
    state.setResetCode("");
    state.setResetPassword("");
    state.setResetConfirmPassword("");
    state.setError(null);
    state.setRecoverySuggestion(null);
  };

  return {
    whoamiAndCheckProduct,
    handleTenantChoice,
    handleProductChoice,
    handleLogin,
    handleNewPasswordSubmit,
    handleMfaCodeSubmit,
    handleEmailCodeSubmit,
    handleChallengeCancel,
    handleTOTPSetupSubmit,
    handleTOTPSetupCancel,
    handlePasskeyLogin,
    handleForgotPassword,
    handleSendResetCode,
    handleResetPasswordSubmit,
    handleResetBack,
    handleResetCancel,
    // PRODUCT_DEV-396
    handleEmailBlur,
    handlePasskeyPromptLater,
    handlePasskeyPromptSetup,
  };
};
